import { PRNG } from 'renderoni';
import type { EnvironmentDef, SegmentKind } from '../../config/environments';
import {
  BUMP_PEAK_SLOPE,
  SMOOTHER_PEAK_SLOPE,
  segmentOffset,
  segmentRise,
  type Segment,
} from './shapes';

export type PickupKind = 'fuel' | 'coin';

export interface PickupSpawn {
  id: number;
  kind: PickupKind;
  x: number;
  y: number;
}

type ProceduralKind = Exclude<SegmentKind, 'flat'>;
const PROCEDURAL_KINDS: ProceduralKind[] = ['rollers', 'crest', 'kicker', 'climb', 'drop', 'washboard'];
/** Keep the route within this band of the start height so parallax layers stay framed. */
const HEIGHT_BAND = 45;
/** Kicker lips may exceed the sustained-climb cap: they are short and taken at speed. */
const KICKER_MAX_LIP_SLOPE = 0.8;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * The fixed, seeded route for one environment. Segments are generated strictly in order
 * from a single PRNG stream, so the route is identical however far ahead it is queried.
 */
export class TerrainProfile {
  readonly segments: Segment[] = [];
  readonly pickups: PickupSpawn[] = [];
  private readonly rng: PRNG;
  private endX = 0;
  private endY = 0;
  private nextFuelAt: number;
  private nextPickupId = 0;
  private lastKind: SegmentKind = 'flat';

  constructor(readonly env: EnvironmentDef) {
    this.rng = new PRNG(env.seed, 1);
    this.nextFuelAt = 0;
    this.buildOpening();
  }

  get generatedTo(): number {
    return this.endX;
  }

  difficultyAt(x: number): number {
    return Math.min(1, Math.max(0, x / this.env.fullDifficultyAt));
  }

  ensure(x: number): void {
    while (this.endX < x) this.generateNext();
  }

  heightAt(x: number): number {
    this.ensure(x + 1);
    const seg = this.segmentAt(x);
    const t = (x - seg.x0) / seg.length;
    return seg.y0 + segmentOffset(seg, t < 0 ? 0 : t > 1 ? 1 : t);
  }

  slopeAt(x: number): number {
    const h = 0.05;
    return (this.heightAt(x + h) - this.heightAt(x - h)) / (2 * h);
  }

  /** Pickups whose x lies in [x0, x1). Generates terrain as needed. */
  pickupsIn(x0: number, x1: number): PickupSpawn[] {
    this.ensure(x1 + 1);
    return this.pickups.filter((p) => p.x >= x0 && p.x < x1);
  }

  /** Next gas can at or beyond x (for the HUD readout). */
  nextFuelAfter(x: number): PickupSpawn | undefined {
    for (let probe = x; probe < x + 2000; probe += 250) {
      this.ensure(probe + 250);
      const found = this.pickups.find((p) => p.kind === 'fuel' && p.x >= x);
      if (found) return found;
    }
    return undefined;
  }

  private segmentAt(x: number): Segment {
    const segs = this.segments;
    let lo = 0;
    let hi = segs.length - 1;
    if (x <= segs[0].x0) return segs[0];
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (segs[mid].x0 <= x) lo = mid;
      else hi = mid - 1;
    }
    return segs[lo];
  }

  private push(kind: SegmentKind, length: number, a = 0, b = 0, n = 1): Segment {
    const seg: Segment = { kind, x0: this.endX, length, y0: this.endY, a, b, n };
    this.segments.push(seg);
    this.endX += length;
    this.endY += segmentRise(seg);
    this.lastKind = kind;
    return seg;
  }

  private addPickup(kind: PickupKind, x: number, lift: number): void {
    const seg = this.segmentAt(x);
    const t = (x - seg.x0) / seg.length;
    const y = seg.y0 + segmentOffset(seg, t);
    this.pickups.push({ id: this.nextPickupId++, kind, x, y: y + lift });
  }

  /** Authored teaching opening (docs/design/03-level-plan.md, 0–215 m). */
  private buildOpening(): void {
    this.push('flat', 20);
    const rollers = this.push('rollers', 36, 0.5, 0, 2);
    this.coinsOverBumps(rollers);
    // Sharp enough (radius ≈ 7 m) that carrying full speed over it lifts the wheels: the first decision.
    const crest = this.push('crest', 16, 2.2);
    this.coinArc(crest.x0 + 3, crest.x0 + 13, 3, 2.2 + 1.0, 1.1);
    const flat = this.push('flat', 58);
    this.addPickup('fuel', 120, 0.9);
    this.coinLine(flat.x0 + 10, 3, 2.5, 0.8);
    // A short, steep launch ramp: 1.4 m over 6 m (lip ≈ 35°) keeps the buggy's speed, then 3 m down to the landing.
    const kicker = this.push('kicker', 24, 1.4, 3.0, 25);
    this.push('flat', 41);
    this.coinArc(kicker.x0 + 7, kicker.x0 + 30, 5, 1.4 + 3.2, 0.6);
    this.nextFuelAt = 120 + this.env.fuelSpacing.start;
  }

  private generateNext(): void {
    const x = this.endX;
    const d = this.difficultyAt(x);

    // Gas can due: place it on a recovery flat so it is always reachable.
    if (x >= this.nextFuelAt - 20) {
      const flat = this.push('flat', lerp(18, 14, d));
      this.addPickup('fuel', flat.x0 + flat.length * 0.6, 0.9);
      this.nextFuelAt = flat.x0 + lerp(this.env.fuelSpacing.start, this.env.fuelSpacing.end, d);
      return;
    }

    const kind = this.pickKind(x);
    switch (kind) {
      case 'rollers': {
        const n = 2 + Math.floor(this.rng.nextFloat() * 3);
        const amp = lerp(0.6, 1.8, d) * lerp(0.8, 1.2, this.rng.nextFloat());
        const wavelength = Math.max(lerp(22, 16, d), (amp * BUMP_PEAK_SLOPE) / this.env.maxSlope);
        const seg = this.push('rollers', n * wavelength, amp, 0, n);
        this.coinsOverBumps(seg);
        break;
      }
      case 'crest': {
        const h = lerp(1.5, 4.5, d) * lerp(0.85, 1.15, this.rng.nextFloat());
        const len = Math.max(lerp(26, 18, d), (h * BUMP_PEAK_SLOPE) / (this.env.maxSlope * 1.1));
        const seg = this.push('crest', len, h);
        this.coinArc(seg.x0 + len * 0.25, seg.x0 + len * 0.75, 3, h + 1.0, 0.8);
        break;
      }
      case 'kicker': {
        // Short, steep launch ramps keep speed. Lip slope is 3·lip/riseLen, capped at KICKER_MAX_LIP_SLOPE.
        const riseLen = lerp(7, 5, d);
        const lip = Math.min(lerp(1.3, 1.8, d), (KICKER_MAX_LIP_SLOPE * riseLen) / 3);
        const dropBelow = lerp(2.0, 5.0, d) * lerp(0.8, 1.2, this.rng.nextFloat());
        const fallLen = Math.max(lerp(14, 10, d), (dropBelow * SMOOTHER_PEAK_SLOPE) / 0.9);
        const total = riseLen + fallLen;
        const seg = this.push('kicker', total, lip, dropBelow, Math.round((riseLen / total) * 100));
        const landing = this.push('flat', lerp(18, 12, d));
        this.coinArc(seg.x0 + riseLen + 1, landing.x0 + 8, 5, lip + 3.0, 0.7);
        break;
      }
      case 'climb': {
        const rise = lerp(4, 12, d) * lerp(0.85, 1.15, this.rng.nextFloat());
        const slope = lerp(0.35, this.env.maxSlope, d);
        this.push('climb', (rise * SMOOTHER_PEAK_SLOPE) / slope, rise);
        this.push('flat', lerp(16, 10, d));
        break;
      }
      case 'drop': {
        const fall = lerp(3, 10, d) * lerp(0.85, 1.15, this.rng.nextFloat());
        const slope = lerp(0.4, 0.9, d);
        this.push('drop', (fall * SMOOTHER_PEAK_SLOPE) / slope, -fall);
        this.push('flat', lerp(16, 10, d));
        break;
      }
      case 'washboard': {
        const n = 5 + Math.floor(lerp(0, 4, d) * this.rng.nextFloat() + 0.5);
        const amp = lerp(0.25, 0.5, d);
        this.push('washboard', n * 3.6, amp, 0, n);
        break;
      }
    }
  }

  private pickKind(x: number): ProceduralKind {
    const startY = 0;
    const heightBias = this.endY - startY;
    let total = 0;
    const weights: number[] = [];
    for (const kind of PROCEDURAL_KINDS) {
      let w = x >= this.env.unlockAt[kind] ? this.env.weights[kind] : 0;
      if (kind === this.lastKind) w *= 0.35; // avoid long repeats
      if (kind === 'climb' && heightBias > HEIGHT_BAND) w = 0;
      if (kind === 'drop' && heightBias < -HEIGHT_BAND) w = 0;
      if (kind === 'climb' && heightBias < -HEIGHT_BAND * 0.5) w *= 3;
      if (kind === 'drop' && heightBias > HEIGHT_BAND * 0.5) w *= 3;
      weights.push(w);
      total += w;
    }
    let r = this.rng.nextFloat() * total;
    for (let i = 0; i < PROCEDURAL_KINDS.length; i++) {
      r -= weights[i];
      if (r < 0) return PROCEDURAL_KINDS[i];
    }
    return 'rollers';
  }

  private coinsOverBumps(seg: Segment): void {
    const wavelength = seg.length / seg.n;
    for (let i = 0; i < seg.n; i++) this.addPickup('coin', seg.x0 + wavelength * (i + 0.5), 1.0);
  }

  /** Coins along an arc between x0 and x1, peaking `peak` metres above the terrain at the arc start. */
  private coinArc(x0: number, x1: number, count: number, peak: number, endLift: number): void {
    const baseY = this.heightAtGenerated(x0);
    for (let i = 0; i < count; i++) {
      const t = count === 1 ? 0.5 : i / (count - 1);
      const x = x0 + (x1 - x0) * t;
      const ground = this.heightAtGenerated(x);
      const arcY = baseY + 4 * peak * t * (1 - t) * 0.8 + endLift;
      this.pickups.push({ id: this.nextPickupId++, kind: 'coin', x, y: Math.max(arcY, ground + endLift) });
    }
  }

  private coinLine(x0: number, count: number, spacing: number, lift: number): void {
    for (let i = 0; i < count; i++) this.addPickup('coin', x0 + i * spacing, lift);
  }

  /** Height lookup restricted to already generated segments (used while placing pickups). */
  private heightAtGenerated(x: number): number {
    const clamped = Math.min(x, this.endX - 1e-6);
    const seg = this.segmentAt(clamped);
    const t = (clamped - seg.x0) / seg.length;
    return seg.y0 + segmentOffset(seg, t);
  }
}
